// Hideo - the guided tour for newcomers, without UI: the steps, where the highlight frame and the bubble go, what the keys
// do. The screen darkens, a frame highlights one element at a time and a bubble explains it (Next / Back / Skip, progress
// dots, arrow keys, Enter and Esc). It starts by itself the first time on each device (after choosing the profile and
// the first project) and can be replayed from How it works, Settings › Info and the Home "First steps".
// State and actions: src/stores/tour.ts; drawing: src/components/TourOverlay.vue.
import { t } from '../i18n'
import type { PageId } from '../navigation'

/** localStorage: 'done' = the tour was seen or skipped on this device (never rename) */
export const TOUR_KEY = 'creatorOS_tour'

/** Phone layout (the same breakpoint as Tailwind's md) */
export { PHONE_QUERY } from '../utils/media'

/** A step of the tour */
interface TourStep {
  /** Page the step opens (none = it stays on the page of the step before) */
  tab?: PageId
  /** Element to highlight, a CSS selector; a function when it changes on phones. None = the bubble in the centre */
  target?: string | ((phone: boolean) => string)
  /** Font Awesome icon of the bubble */
  icon: string
  /** Title (plain text) */
  title: () => string
  /** Text (HTML with <b>; texts of the code, never user data); phone = the phone layout when the step is shown */
  text: (phone: boolean) => string
}

export const TOUR_STEPS: readonly TourStep[] = [
  { tab: 'home', icon: 'fa-flag', title: () => t('Welcome to Hideo'),
    text: () => t('Hideo takes you from the idea to the published video: ideas, scripts, calendar, creators and sources in one place. I\'ll show you how it works in less than a minute.') },
  { tab: 'home', target: '#ideaDrop', icon: 'fa-inbox', title: () => t('Drop your idea here'),
    text: () => t('Got an idea? Write it down, add a photo or a voice note if you like, then press <b>Send to the plan</b>: it becomes a video in the "Idea" column.') },
  { tab: 'home', target: '#home-next', icon: 'fa-house', title: () => t('Your Home'),
    text: () => t('Below the idea you find your next releases (slide them with the arrows) and what to do and what is overdue. Your photo at the top opens your profile.') },
  { tab: 'ped', target: '#pedBoard', icon: 'fa-layer-group', title: () => t('The plan'),
    text: phone => [t('The path of every piece of content, in five columns: from <b>Idea</b> to <b>Ready</b>.'),
      phone ? t('Swipe sideways to see them all; the status is changed from the video dialog.') : t('Drag a card from one column to another to move it forward.'),
      t('At the top you choose the <b>plan</b> (one or more projects together, with "+ Plan") and filter the videos by <b>format</b> (e.g. "Super Mario 64").')].join(' ') },
  { tab: 'ped', target: '#fabBtn', icon: 'fa-plus', title: () => t('New video and SEO'),
    text: () => t('With this button you create a video. In its dialog you first choose the content <b>Type</b>; in <b>Details</b> you find title, thumbnail, script with editor and teleprompter, release date, checklist and notes. The second tab is the last step and changes with the Type: for a long video, title, description, chapters and tags ready for YouTube, with a score.') },
  { tab: 'keywords', target: '#kwBody', icon: 'fa-arrow-trend-up', title: () => t('Trends'),
    text: () => t('Write a topic and find out what people really search for on YouTube, and how much it is searched over time on Google Trends. Save the good ideas in your bank and turn them into a video or a hook with one tap.') },
  { tab: 'calendar', target: '#cal-grid', icon: 'fa-calendar-days', title: () => t('Calendar'),
    text: () => t('The plan\'s releases, month by month. Tap a day to create a video with that date, drag a video to move it. Board, Calendar and Timeline open from the tabs at the top of the plan. When a video is out, <b>Mark as published</b> moves it to the <b>Published videos</b> page.') },
  { tab: 'vault', target: '#vault-tags', icon: 'fa-anchor', title: () => t('Tools: Hooks'),
    text: () => t('Your archive of hooks and opening lines, organised with tags. A hook can become the base of a script.') },
  { tab: 'creators', target: '#creators-grid', icon: 'fa-user-group', title: () => t('Creators'),
    text: () => t('The creators you follow, split into <b>Collaborators</b> and <b>Competitors</b>: tap one of the two big buttons to open the list. For each one you note strengths and weaknesses and analyse their videos.') },
  { tab: 'sources', target: '#sources-list', icon: 'fa-link', title: () => t('Sources'),
    text: () => t('Your sources (articles, papers, TV, sites…) with fact-checking notes. As in Creators, there is a big button for each <b>category</b> (e.g. Scientific paper, TV): tap it to open its sources. The <b>tags</b> above are for the themes (e.g. Nintendo, Statistics).') },
  { target: phone => phone ? '#mobileProfileBtn' : '#projectBtn', icon: 'fa-diagram-project', title: () => t('Projects'),
    text: phone => (phone ? t('Tap your profile to choose the <b>project</b> you are working on: a channel, a client, a business.') : t('Here you choose the <b>project</b> you are working on: a channel, a client, a business.'))
      + ' ' + t('Each one has its own videos, hooks, creators and sources; with "All projects" you see them together. You create new ones from "Manage projects".') },
  { target: phone => phone ? '#mobileProfileBtn' : '#profileBtn', icon: 'fa-user', title: () => t('Profile and online save'),
    text: () => t('Your profile. From here you use <b>Save online</b> and <b>Load online</b> with Google Drive: your data, images and voice notes are saved there and you find them on both your phone and your computer. I recommend doing it right away.') },
  { target: phone => phone ? '#mobileMenuBtn' : '#nav-settings', icon: 'fa-gear', title: () => t('Settings and guide'),
    text: phone => phone ? t('In the <b>☰</b> menu you find every page, Search, Settings (theme, colour, checklist, autosave) and <b>How it works</b>, the full guide.')
      : t('Here you find <b>Settings</b> (theme, colour, checklist, autosave) and, just above, <b>How it works</b>: the full guide.') },
  { tab: 'home', icon: 'fa-flag-checkered', title: () => t('You\'re ready!'),
    text: () => t('Start by putting your first idea in the Home box: on the Home you also find the <b>First steps</b> to follow. You can watch this tour again whenever you like from How it works or from Settings.') }
]

/** The selector of the element a step highlights, '' = none */
export const stepTarget = (st: TourStep, phone: boolean) => (typeof st.target === 'function' ? st.target(phone) : st.target) || ''

/** A step number kept inside the tour */
export const clampStep = (i: number, total = TOUR_STEPS.length) => Math.max(0, Math.min(total - 1, i))

/** A box on screen, in pixels */
interface Rect {
  x: number
  y: number
  w: number
  h: number
}

/** The highlight frame around an element (8 px around it, inside the screen with a 6 px margin); null = the element is
 *  too small or off screen (the frame shrinks to a point in the centre) */
export function spotRect(b: { left: number; top: number; right: number; bottom: number }, vw: number, vh: number): Rect | null {
  const pad = 8
  const x = Math.max(6, b.left - pad), y = Math.max(6, b.top - pad)
  const r = { x, y, w: Math.min(vw - 6, b.right + pad) - x, h: Math.min(vh - 6, b.bottom + pad) - y }
  return r.w < 4 || r.h < 4 ? null : r
}

/** Inline style of the frame: around the element, or a point in the centre of the screen */
export function spotStyle(r: Rect | null, vw: number, vh: number) {
  return r ? { left: r.x + 'px', top: r.y + 'px', width: r.w + 'px', height: r.h + 'px', borderRadius: '1rem' }
    : { left: vw / 2 + 'px', top: vh / 2 + 'px', width: '0px', height: '0px', borderRadius: '50%' }
}

/** Where the bubble (tw × th) goes: below the frame if there is room, otherwise above, otherwise at the bottom of the
 *  screen; centred on the frame and inside the screen (16 px margin). Without a frame, in the centre of the screen */
export function tipPos(r: Rect | null, tw: number, th: number, vw: number, vh: number): { left: number; top: number } {
  const m = 16
  let tx: number, ty: number
  if (!r) { tx = (vw - tw) / 2; ty = (vh - th) / 2 }
  else {
    const below = vh - (r.y + r.h), above = r.y
    if (below >= th + m) ty = r.y + r.h + 12
    else if (above >= th + m) ty = r.y - th - 12
    else ty = vh - th - m
    tx = Math.min(Math.max(m, r.x + r.w / 2 - tw / 2), vw - tw - m)
  }
  return { left: Math.max(m, tx), top: Math.max(m, ty) }
}

/** A key during the tour: 'skip' (Esc), 'finish' (→ or Enter on the last step), the step to show (→ / Enter, ←), or
 *  null = not the tour's key (it goes on to the rest of the app) */
export function tourKey(key: string, i: number, total = TOUR_STEPS.length): 'skip' | 'finish' | number | null {
  if (key === 'Escape') return 'skip'
  if (key === 'ArrowRight' || key === 'Enter') return i === total - 1 ? 'finish' : i + 1
  if (key === 'ArrowLeft') return i - 1
  return null
}
